'use client';

import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import type { useCatalogFilterOptions } from '@/features/seo-listing/hooks';
import { buildFilterOptionsCacheKey } from '@/features/seo-listing/utils';
import type { FilterState } from '../store';
import {
  buildFilterReloadContext,
  type FilterReloadContext,
  getBrowserPathname,
  type ListingIdentityFilters,
  listingIdentityFromFilterState,
} from '../utils';

type ReloadFilterOptions = ReturnType<typeof useCatalogFilterOptions>['reload'];

interface UseListingFilterOptionsSyncParams {
  pathname: string;
  filterState: FilterState;
  isSeoPendingRef: RefObject<boolean>;
  reloadFilterOptions: ReloadFilterOptions;
  initialSelectedFandoms: string[];
  initialSelectedTags: string[];
  initialSelectedUniverses: string[];
  initialSelectedCharacters: string[];
  initialSelectedPairingCharacters: string[];
}

/**
 * Keeps filter dictionaries (facet options) in sync with the listing identity
 * and remembers per-path identity/context for popstate and SEO settle handlers.
 */
export const useListingFilterOptionsSync = ({
  pathname,
  filterState,
  isSeoPendingRef,
  reloadFilterOptions,
  initialSelectedFandoms,
  initialSelectedTags,
  initialSelectedUniverses,
  initialSelectedCharacters,
  initialSelectedPairingCharacters,
}: UseListingFilterOptionsSyncParams) => {
  const initialFilterReloadPath = pathname;
  const initialFilterReloadContext = buildFilterReloadContext(initialFilterReloadPath, {
    selectedFandoms: initialSelectedFandoms,
    selectedCharacters: initialSelectedCharacters,
    selectedTags: initialSelectedTags,
    selectedUniverses: initialSelectedUniverses,
    selectedPairingCharacters: initialSelectedPairingCharacters,
  });
  // Seed the cache key from the same elevated context SSR used for filter
  // dictionaries (e.g. /fanfics?fandoms=… → pageKind fandom) so the mount
  // effect does not immediately re-fetch all facet APIs.
  const filterOptionsKeyRef = useRef(
    buildFilterOptionsCacheKey(
      initialFilterReloadContext.pageKind,
      initialFilterReloadContext.lockedFacets,
    ),
  );

  const filterContextByPathRef = useRef(new Map([[pathname, initialFilterReloadContext]]));
  const filterIdentityByPathRef = useRef(
    new Map<string, ListingIdentityFilters>([
      [
        initialFilterReloadPath,
        listingIdentityFromFilterState({
          selectedFandoms: initialSelectedFandoms,
          selectedCharacters: initialSelectedCharacters,
          selectedTags: initialSelectedTags,
          selectedPairingCharacters: initialSelectedPairingCharacters,
        }),
      ],
    ]),
  );
  const [listingPathEpoch, setListingPathEpoch] = useState(0);

  const syncRelatedFilterOptions = useCallback(
    (context: FilterReloadContext) => {
      // During SEO pending the address bar still points at the previous
      // permalink while filterState already has the next identity — do not
      // overwrite that path's remembered context/options key source.
      if (!isSeoPendingRef.current) {
        filterContextByPathRef.current.set(getBrowserPathname(), context);
      }
      const nextKey = buildFilterOptionsCacheKey(context.pageKind, context.lockedFacets);
      if (nextKey === filterOptionsKeyRef.current) return;
      filterOptionsKeyRef.current = nextKey;
      void reloadFilterOptions({
        ...context,
        fetchMode: 'clientReload',
      });
    },
    [isSeoPendingRef, reloadFilterOptions],
  );

  // Reload facet dictionaries when listing *identity* changes (fandom/tag),
  // not when pass-through filters like character are toggled — character pick
  // must keep the fandom-scoped list loaded after fandom select.
  // Skip while SEO redirect is in flight: address bar may still show the old
  // permalink while filters are already cleared; settled handler reloads then.
  useEffect(() => {
    if (isSeoPendingRef.current) return;
    syncRelatedFilterOptions(
      buildFilterReloadContext(getBrowserPathname(), {
        selectedFandoms: filterState.selectedFandoms,
        selectedCharacters: filterState.selectedCharacters,
        selectedTags: filterState.selectedTags,
        selectedUniverses: filterState.selectedUniverses,
        selectedPairingCharacters: filterState.selectedPairingCharacters,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on listing identity change only (fandoms/tags)
  }, [
    filterState.selectedFandoms,
    filterState.selectedTags,
    isSeoPendingRef,
    syncRelatedFilterOptions,
  ]);

  return {
    syncRelatedFilterOptions,
    filterContextByPathRef,
    filterIdentityByPathRef,
    listingPathEpoch,
    setListingPathEpoch,
  };
};
